import { titleize } from "@/helpers/filters";
import type { RouteMeta } from "vue-router";

export type PlatformFeature = {
  key: string;
  name: string;
  description: string | null;
  nav_group: string;
  nav_group_name: string;
  sort_order: number;
  enabled: boolean;
};

export type DisabledPlatformFeature = {
  key: string;
  name?: string;
};

export type PlatformFeatureGroup = {
  id: string;
  name: string;
  features: PlatformFeature[];
};

type RouteNode = {
  path?: string;
  name?: string | symbol;
  redirect?: unknown;
  meta?: RouteMeta & {
    menuIcon?: string;
    menuLabel?: string;
    title?: string;
    killSwitch?: boolean;
    platformFeature?: string;
    breadcrumb?: string | { title?: string; to?: { name?: string } };
  };
  children?: RouteNode[];
};

function breadcrumbToName(meta?: RouteNode["meta"]): string | null {
  const breadcrumb = meta?.breadcrumb;
  if (
    breadcrumb &&
    typeof breadcrumb === "object" &&
    typeof breadcrumb.to?.name === "string"
  ) {
    return breadcrumb.to.name;
  }
  return null;
}

function routeName(route: RouteNode): string | null {
  return typeof route.name === "string" ? route.name : null;
}

export function killSwitchKeyFromRoute(route: RouteNode): string | null {
  if (route.meta?.killSwitch === false) return null;
  if (route.meta?.menuIcon) return null;
  if (route.meta?.platformFeature) return route.meta.platformFeature;
  return routeName(route) ?? breadcrumbToName(route.meta);
}

export function routeDisplayName(route: RouteNode): string {
  if (route.meta?.menuLabel) return route.meta.menuLabel;
  if (typeof route.meta?.title === "string") return route.meta.title;
  const breadcrumb = route.meta?.breadcrumb;
  if (typeof breadcrumb === "string") return breadcrumb;
  if (breadcrumb && typeof breadcrumb === "object" && breadcrumb.title) {
    return breadcrumb.title;
  }
  const name = routeName(route);
  return name ? titleize(name.replace(/_/g, " ")) : "Untitled";
}

export function killableFeaturesFromRoutes(
  rootChildren: RouteNode[] = [],
): Omit<PlatformFeature, "enabled">[] {
  const features: Omit<PlatformFeature, "enabled">[] = [];

  rootChildren.forEach((group) => {
    if (!group.meta?.menuIcon || !group.meta?.breadcrumb) return;
    const pathKey = (group.path || "").split("/").pop() || "";
    const nav_group = pathKey.replace(/-/g, "_");
    const nav_group_name =
      typeof group.meta.breadcrumb === "string"
        ? group.meta.breadcrumb
        : group.meta.breadcrumb.title || nav_group;

    (group.children ?? []).forEach((route, index) => {
      if (route.redirect) return;
      const key = killSwitchKeyFromRoute(route);
      if (!key) return;
      features.push({
        key,
        name: routeDisplayName(route),
        description: null,
        nav_group,
        nav_group_name,
        sort_order: (index + 1) * 10,
      });
    });
  });

  return features;
}

export function overlayFeatureEnabledState(
  catalog: Omit<PlatformFeature, "enabled">[],
  disabledKeys: Iterable<string>,
): PlatformFeature[] {
  const disabled = new Set(disabledKeys);
  return catalog.map((feature) => ({
    ...feature,
    enabled: !disabled.has(feature.key),
  }));
}

export function groupPlatformFeatures(
  features: PlatformFeature[],
): PlatformFeatureGroup[] {
  const groups: PlatformFeatureGroup[] = [];
  const byId = new Map<string, PlatformFeatureGroup>();

  for (const feature of features) {
    let group = byId.get(feature.nav_group);
    if (!group) {
      group = {
        id: feature.nav_group,
        name: feature.nav_group_name,
        features: [],
      };
      byId.set(feature.nav_group, group);
      groups.push(group);
    }
    group.features.push(feature);
  }

  for (const group of groups) {
    group.features.sort(
      (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name),
    );
  }

  return groups;
}

export function matchedPlatformFeatureKey(to: {
  name?: string | symbol | null;
  meta?: RouteNode["meta"];
  matched?: RouteNode[];
}): string | null {
  const matched = to.matched ?? [];
  const underNavGroup = matched.some((record) => record.meta?.menuIcon);
  if (!underNavGroup) {
    if (to.meta?.killSwitch === false) return null;
    const feature = to.meta?.platformFeature;
    return typeof feature === "string" ? feature : null;
  }
  for (const record of matched) {
    const key = killSwitchKeyFromRoute(record);
    if (key) return key;
  }
  return null;
}

export function disabledPlatformFeatureMessage(
  featureName?: string | null,
  featureKey?: string | null,
): string {
  const name =
    featureName || (featureKey ? titleize(featureKey) : "This feature");
  return `${name} is temporarily unavailable. Please try again later.`;
}

export function disabledPlatformFeatureFallback(currentRouteName?: unknown): {
  name: "home" | "account";
} {
  if (currentRouteName === "audience_explorer" || currentRouteName === "home") {
    return { name: "account" };
  }
  return { name: "home" };
}
